import ImageGallery from '@/components/image-gallery-custom';
import ProductCartActionsCustom from '@/components/product-cart-actions-custom';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isGiftCardProduct } from '@/lib/gift-card-product-utils';
import { getMultipackContentProduct } from '@/lib/multipack';
import { isProductBundle, isProductSet } from '@/lib/product-utils';
import { ProductGiftProvider } from '@/providers/product-gift';
import ProductViewProvider from '@/providers/product-view';
import type { ShopperProducts } from '@/scapi';
import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import ProductInfoCard from '../product-info-card-custom';
import ProductInfoPrice from './product-info-price';
import { default as ProductInfoTitle } from './product-info-title';
import type { BrandContent } from '@/lib/api/fetch-brand';
import type { SupplyChainContent } from '@/lib/api/fetch-supply-chain';
import ProductTabs from './product-tabs';
import { ProductTabsSkeleton } from './product-tabs/product-tabs-skeleton';
import GiftCardProductTabsFallback from './gift-card-tabs/gift-card-product-tabs-fallback';
import GiftCardCheckBalance from './gift-card-check-balance';
import GiftCardPdpFaqs, { GiftCardPdpFaqsFallback } from './gift-card-pdp-faqs';
import PromoBanner from './promo-banner';
import { PDP_DETAILS_ANCHOR_ID, pdpAnchorScrollMarginClass } from './pdp-sticky-anchor-bar';
import { SvgHelp, SvgShipping } from '../icons-eataly';
import { Trans, useTranslation } from 'react-i18next';
import CustomerCareLink from '@/components/customer-care-link';
import { useCustomerCareHref } from '@/hooks/use-customer-care-href';
import { cn } from '@/lib/utils';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { HelpCenterCategory } from '@/lib/api/help-center.server';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
    brandCustomObject: Promise<BrandContent | undefined>;
    supplyChainCustomObject: Promise<SupplyChainContent | undefined>;
    faqCategoryPromise?: Promise<HelpCenterCategory | null>;
    page: Promise<PageWithComponentData | null> | PageWithComponentData | null;
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 */
export default function ProductView({
    product,
    brandCustomObject,
    supplyChainCustomObject,
    page,
    faqCategoryPromise,
}: ProductViewProps): ReactElement {
    const { t } = useTranslation('common');
    const { t: tProduct } = useTranslation('product');
    const isGiftCard = isGiftCardProduct(product);
    const hasContactsLink = Boolean(useCustomerCareHref('contacts'));
    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);
    const contentProduct = getMultipackContentProduct(product);
    // Get selected attributes from URL parameters for image gallery
    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages } = useProductImages({
        product,
        selectedAttributes,
    });
    function renderProductPromotionCards() {
        if (!product.productPromotions) return null;
        const promotionalPromotions = product.productPromotions.filter(
            (promotion) => promotion.promotionalPrice === product.price
        );
        if (promotionalPromotions.length === 0) return null;
        return (
            <ul className="mt-6 flex flex-col gap-4">
                {promotionalPromotions.map((promotion) => (
                    <li key={promotion.promotionId}>
                        <ProductInfoCard richText={promotion.calloutMsg} />
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <ProductGiftProvider>
            <ProductViewProvider product={product} mode="add">
                <div className="grid grid-global max-lg:px-0!">
                    <div className="grid grid-cols-subgrid col-span-full max-w-screen-2xl mx-auto lg:grid-rows-[auto_1fr] lg:pb-9">
                        {/* Single title: full-width above gallery on mobile, right column on desktop */}
                        <div className="col-span-full mb-6 max-lg:px-6 lg:col-span-4 lg:col-end-12 lg:row-start-1 lg:mb-8 lg:pt-6">
                            <ProductInfoTitle product={product} />
                        </div>
                        {/* Left Column - Image Gallery + Description */}
                        <div className="col-span-6 max-lg:px-6 lg:col-span-5 lg:col-start-2 lg:row-span-2 lg:row-start-1">
                            <ImageGallery
                                // Stable across gift-card cut / variant pid swaps so the gallery
                                // does not remount (and re-request images) on every size click.
                                key={product.master?.masterId ?? product.id}
                                images={galleryImages}
                                eager={!isProductASet && !isProductABundle}
                                showNavigationArrows
                                navigationArrowSize="lg"
                                productName={product.name}
                                product={product}
                            />
                        </div>

                        {/* Right Column - Product Info */}
                        <div className="col-span-6 py-6 max-lg:px-6 lg:col-span-4 lg:col-end-12 lg:row-start-2 lg:self-start lg:pt-0">
                            <div className="flex flex-col gap-6 lg:gap-8">
                                <ProductInfoPrice product={product} />
                                <ProductCartActionsCustom product={product} />
                            </div>
                            {/* <Faq /> */}
                            <div className="mt-6 flex flex-col gap-2 text-neutral-40">
                                {isGiftCard && (
                                    <div className="flex items-center gap-2">
                                        <SvgShipping className="size-6 shrink-0" aria-hidden="true" />
                                        <p className="body-copy-m">
                                            {tProduct('giftCard.instantEmailDelivery', {
                                                defaultValue: 'Ricevila istantaneamente per email',
                                            })}
                                        </p>
                                    </div>
                                )}
                                {hasContactsLink && (
                                    <div className="flex items-center gap-2">
                                        <SvgHelp aria-hidden="true" />
                                        <Trans
                                            t={t}
                                            i18nKey="needHelp"
                                            components={{
                                                a: (
                                                    <CustomerCareLink
                                                        kind="contacts"
                                                        className="body-copy-m-bold underline underline-offset-2 decoration-solid"
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {renderProductPromotionCards()}
                        </div>
                    </div>
                </div>
                <div className="bg-neutral-white">
                    <div className="grid-global">
                        <div
                            id={PDP_DETAILS_ANCHOR_ID}
                            className={cn(
                                'col-span-full lg:col-span-10 lg:col-start-2 pt-6 flex flex-col gap-8 lg:gap-20',
                                pdpAnchorScrollMarginClass
                            )}>
                            {isGiftCard ? (
                                <>
                                    <Suspense fallback={<ProductTabsSkeleton />}>
                                        <Await resolve={page}>
                                            {(resolvedPage) => (
                                                <Region
                                                    page={resolvedPage}
                                                    regionId="productTabs"
                                                    fallbackElement={<ProductTabsSkeleton />}
                                                    errorElement={<GiftCardProductTabsFallback product={product} />}
                                                />
                                            )}
                                        </Await>
                                    </Suspense>
                                </>
                            ) : (
                                <>
                                    <ProductTabs
                                        product={contentProduct}
                                        labelImageProduct={product}
                                        brandCustomObject={brandCustomObject}
                                        supplyChainCustomObject={supplyChainCustomObject}
                                    />
                                    <PromoBanner />
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isGiftCard && (
                    <div className="bg-sand-95 col-span-full grid-global">
                        <GiftCardCheckBalance />
                    </div>
                )}
                {isGiftCard && faqCategoryPromise ? (
                    <Suspense fallback={<GiftCardPdpFaqsFallback />}>
                        <Await resolve={faqCategoryPromise}>
                            {(category) => (category ? <GiftCardPdpFaqs category={category} noSectionNames /> : null)}
                        </Await>
                    </Suspense>
                ) : null}
            </ProductViewProvider>
        </ProductGiftProvider>
    );
}
